/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\components\CommandeFournisseur\PreviewModal.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

import React, { useContext } from 'react';
import { CommandeGroupee } from '../../services/CommandeFournisseurService';
import AuthContext from '../../contexts/AuthContext';

interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  commande: CommandeGroupee | null;
  isGenerating: boolean;
  onExport: (format: 'pdf' | 'excel' | 'csv' | 'txt') => Promise<void>;
}

const PreviewModal: React.FC<PreviewModalProps> = ({
  isOpen,
  onClose,
  commande,
  isGenerating,
  onExport,
}) => {
  const { access, startTrial } = useContext(AuthContext);
  const canExport = access?.access_mode === 'active' || access?.access_mode === 'trial';

  if (!isOpen || !commande) return null;

  const formatMontant = (montant: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(montant);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        {/* En-tête */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Aperçu de la commande fournisseur
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Vérifiez les données avant génération du fichier
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Informations générales */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Numéro</p>
              <p className="text-sm font-medium text-gray-900">{commande.numeroCommande}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Date</p>
              <p className="text-sm font-medium text-gray-900">{formatDate(commande.dateCreation)}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Devis inclus</p>
              <p className="text-sm font-medium text-gray-900">{commande.totaux.nombreDevis}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total</p>
              <p className="text-sm font-medium text-primary-600">{formatMontant(commande.totaux.montantTotal)}</p>
            </div>
          </div>
        </div>

        {/* Statistiques */}
        {commande.statistiques && (
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <h4 className="text-sm font-medium text-blue-900 mb-2">📊 Statistiques</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-blue-700">Matières exclues (en stock)</p>
                <p className="text-lg font-semibold text-blue-900">{commande.statistiques.matieresExcluesStock}</p>
              </div>
              <div>
                <p className="text-xs text-blue-700">Matières à commander</p>
                <p className="text-lg font-semibold text-blue-900">{commande.statistiques.matieresACommander}</p>
              </div>
            </div>
          </div>
        )}

        {/* Contenu scrollable */}
        <div className="max-h-96 overflow-y-auto">
          {/* Matières */}
          {commande.matieres.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                🧵 Matières à commander ({commande.matieres.length})
                <span className="ml-2 text-xs text-gray-500">
                  {formatMontant(commande.totaux.montantMatieres)}
                </span>
              </h4>
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Matière</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quantité</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Prix unit.</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Devis</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {commande.matieres.map((matiere, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-sm text-gray-900">{matiere.nom}</td>
                        <td className="px-3 py-2 text-sm text-gray-500">
                          {matiere.quantiteMetres.toFixed(2)} ml
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-500">
                          {formatMontant(matiere.prixUnitaire)}/ml
                        </td>
                        <td className="px-3 py-2 text-sm font-medium text-gray-900">
                          {formatMontant(matiere.montantTotal)}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500">
                          {matiere.devisOrigines.map(d => d.reference).join(', ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Fournitures */}
          {commande.fournitures.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                🔧 Fournitures ({commande.fournitures.length})
                <span className="ml-2 text-xs text-gray-500">
                  {formatMontant(commande.totaux.montantFournitures)}
                </span>
              </h4>
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fourniture</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quantité</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Prix unit.</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Devis</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {commande.fournitures.map((fourniture, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-sm text-gray-900">{fourniture.nom}</td>
                        <td className="px-3 py-2 text-sm text-gray-500">
                          {fourniture.quantite.toFixed(2)} {fourniture.unite}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-500">
                          {formatMontant(fourniture.prixUnitaire)}/{fourniture.unite}
                        </td>
                        <td className="px-3 py-2 text-sm font-medium text-gray-900">
                          {formatMontant(fourniture.montantTotal)}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500">
                          {fourniture.devisOrigines.map(d => d.reference).join(', ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Devis inclus */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-900 mb-3">
              📋 Devis inclus dans cette commande
            </h4>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex flex-wrap gap-2">
                {commande.devisInclus.map((reference, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {reference}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Résumé des totaux */}
        <div className="border-t border-gray-200 pt-4 mt-6">
          <div className="flex justify-between items-center">
            <div className="grid grid-cols-3 gap-4 flex-1">
              <div className="text-center">
                <p className="text-xs text-gray-500">Matières</p>
                <p className="text-sm font-medium text-gray-900">
                  {formatMontant(commande.totaux.montantMatieres)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500">Fournitures</p>
                <p className="text-sm font-medium text-gray-900">
                  {formatMontant(commande.totaux.montantFournitures)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500">Total général</p>
                <p className="text-lg font-bold text-primary-600">
                  {formatMontant(commande.totaux.montantTotal)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-3 mt-6 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-200"
          >
            Annuler
          </button>
          
          <div className="flex flex-wrap gap-2">
            {/* Export PDF */}
            <button
              onClick={async () => {
                if (!canExport) {
                  await startTrial().catch(() => {});
                  return;
                }
                await onExport('pdf');
              }}
              disabled={isGenerating}
              className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              title="Exporter en PDF (impression)"
            >
              <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              📄 PDF
            </button>

            {/* Export Excel */}
            <button
              onClick={async () => {
                if (!canExport) {
                  await startTrial().catch(() => {});
                  return;
                }
                await onExport('excel');
              }}
              disabled={isGenerating}
              className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              title="Exporter en Excel (CSV)"
            >
              <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              📊 Excel
            </button>

            {/* Export CSV */}
            <button
              onClick={async () => {
                if (!canExport) {
                  await startTrial().catch(() => {});
                  return;
                }
                await onExport('csv');
              }}
              disabled={isGenerating}
              className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-gray-600 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              title="Exporter en CSV simple"
            >
              <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              📋 CSV
            </button>

            {/* Export TXT (original) */}
            <button
              onClick={async () => {
                await onExport('txt');
              }}
              disabled={isGenerating}
              className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              title="Exporter en format texte simple"
            >
              {isGenerating ? (
                <>
                  <svg className="animate-spin -ml-1 mr-1 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  ...
                </>
              ) : (
                <>
                  <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  📝 TXT
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;
